/**
 * AviTrashModal — spec rev159 (2026-02-07) Lot A-bis.
 *
 * Modale « Corbeille AVI » — accessible via le bouton Corbeille du
 * header AVI, réservée aux admins. Affiche la liste des demandes
 * ``is_deleted=true`` avec pour chaque ligne :
 *   • Sujet + type (badge) + priorité
 *   • Auteur de la suppression + date/heure
 *   • Actions : Restaurer (renvoie dans la liste) · Purger (irréversible)
 *
 * Sync temps réel : recharge à chaque ouverture ; les événements SSE
 * ``avi.trashed`` / ``avi.restored`` sont gérés côté page parente
 * (useAviStream) qui rafraîchit la liste principale.
 */
import { useEffect, useState } from "react";
import { toast } from "@/lib/toast";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import DeskConfirm from "@/components/crm/DeskConfirm";
import UserAvatar from "@/components/ui/UserAvatar";
import { Trash2, RotateCcw, AlertTriangle, Inbox } from "lucide-react";

export default function AviTrashModal({ open, onOpenChange, aviType, onChanged }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirmPurge, setConfirmPurge] = useState(null); // id à purger

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/avi/trash/list", { params: aviType ? { avi_type: aviType } : {} });
      setItems(data || []);
    } catch (e) {
      toast.error("Corbeille indisponible", { description: e?.response?.data?.detail || "" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line
  }, [open, aviType]);

  const doRestore = async (id) => {
    try {
      await api.post(`/avi/${id}/restore`);
      toast.success("Demande restaurée");
      onChanged && onChanged();
      load();
    } catch (e) {
      toast.error("Restauration échouée", { description: e?.response?.data?.detail || "" });
    }
  };

  const doPurge = async (id) => {
    try {
      await api.delete(`/avi/${id}/purge`);
      toast.success("Demande purgée définitivement");
      setConfirmPurge(null);
      load();
    } catch (e) {
      toast.error("Purge échouée", { description: e?.response?.data?.detail || "" });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl"
        style={{ background: "#141414", border: "1px solid #2A2A2A" }}
        data-testid="avi-trash-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[16px]">
            <Trash2 size={16} style={{ color: "#EF4444" }} />
            Corbeille AVI {items.length > 0 && (
              <span className="text-[12px] font-normal" style={{ color: "#9CA3AF" }}>
                — {items.length} demande{items.length > 1 ? "s" : ""}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="mt-2 text-[11.5px] flex items-center gap-2 flex-wrap" style={{ color: "#9CA3AF" }}>
          <span>
            Les demandes restent dans la corbeille <strong>30 jours</strong> avant
            purge automatique. Vous pouvez les restaurer ou les purger
            définitivement à tout moment.
          </span>
          {/* Spec rev160 (2026-02-07) — compteur "purge imminente" : nombre
              de demandes supprimées il y a >= 23 jours (donc à J-7 de la
              purge auto 30j). Anticipation visuelle. */}
          {(() => {
            const now = Date.now();
            const imminent = items.filter((it) => {
              if (!it.deleted_at) return false;
              const del = new Date(it.deleted_at).getTime();
              const days = (now - del) / 86_400_000;
              return days >= 23; // reste ≤ 7j avant purge
            }).length;
            if (imminent === 0) return null;
            return (
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-semibold whitespace-nowrap"
                style={{
                  background: "rgba(224,165,90,0.14)",
                  border: "1px solid rgba(224,165,90,0.45)",
                  color: "#e0a55a",
                }}
                title="Ces demandes sont dans la corbeille depuis 23 jours ou plus — plus que 7 jours avant purge définitive"
                data-testid="avi-trash-imminent-count"
              >
                <AlertTriangle size={11} strokeWidth={2.2} />
                {imminent} purge{imminent > 1 ? "s" : ""} dans les 7 prochains jours
              </span>
            );
          })()}
        </div>

        {loading ? (
          <div className="py-10 text-center text-[13px]" style={{ color: "#9CA3AF" }}>
            Chargement…
          </div>
        ) : items.length === 0 ? (
          <div className="py-10 text-center" data-testid="avi-trash-empty">
            <Inbox size={36} className="mx-auto mb-2" style={{ color: "#3A3A3F" }} />
            <div className="text-[13px]" style={{ color: "#9CA3AF" }}>
              Aucune demande dans la corbeille.
            </div>
          </div>
        ) : (
          <ul className="mt-3 max-h-[420px] overflow-y-auto space-y-2 pr-1" data-testid="avi-trash-list">
            {items.map((it) => (
              <li
                key={it.id}
                className="flex items-center gap-3 rounded-lg px-3 py-2.5"
                style={{ background: "#1A1A1A", border: "1px solid #2A2A2A" }}
                data-testid={`avi-trash-item-${it.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold text-white truncate">
                    {it.subject || "(sans objet)"}
                  </div>
                  <div className="text-[11px] mt-0.5 tabular-nums flex items-center gap-1.5" style={{ color: "#9CA3AF" }}>
                    <span>Supprimée par</span>
                    {/* Spec iter82 (Feb 2026) — nom remplacé par avatar
                        cliquable (popover). */}
                    <UserAvatar
                      user={{
                        id: it.deleted_by || it.deleted_by_name,
                        first_name: (it.deleted_by_name || "").split(" ")[0] || "",
                        last_name: (it.deleted_by_name || "").split(" ").slice(1).join(" ") || "",
                      }}
                      size={18}
                      subtitle="Auteur de la suppression"
                    />
                    <span className="opacity-50">·</span>
                    {it.deleted_at
                      ? new Date(it.deleted_at).toLocaleString("fr-FR")
                      : "—"}
                  </div>
                  {/* Spec iter140 pt.4 (Feb 2026) — colonne « MOTIF ».
                      Affiche le motif de suppression saisi lors du
                      soft-delete (parité corbeille Documents). Absent
                      pour les demandes supprimées avant iter139. */}
                  {it.deleted_reason && (
                    <div className="text-[11px] mt-1 inline-flex items-start gap-1.5 max-w-full"
                         data-testid={`avi-trash-item-${it.id}-reason`}>
                      <span className="uppercase tracking-wide text-[9.5px] font-bold pt-0.5"
                            style={{ color: "#F87171", opacity: 0.85 }}>
                        Motif&nbsp;:
                      </span>
                      <span className="italic truncate max-w-[420px]" style={{ color: "#F2C7C7" }}
                            title={it.deleted_reason}>
                        {it.deleted_reason}
                      </span>
                    </div>
                  )}
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => doRestore(it.id)}
                  data-testid={`avi-trash-restore-${it.id}`}
                >
                  <RotateCcw size={12} className="mr-1" /> Restaurer
                </Button>
                <Button
                  size="sm"
                  onClick={() => setConfirmPurge(it.id)}
                  className="text-white border-0"
                  style={{ background: "#EF4444" }}
                  data-testid={`avi-trash-purge-${it.id}`}
                >
                  <Trash2 size={12} className="mr-1" /> Purger
                </Button>
              </li>
            ))}
          </ul>
        )}

        {/* Confirmation purge · R2 (iter393) */}
        <DeskConfirm open={!!confirmPurge} title="Purge définitive ?" message="La demande, ses messages, ses pièces jointes et ses notifications seront irrémédiablement supprimés."
          confirmLabel="Purger" onClose={() => setConfirmPurge(null)} onConfirm={() => doPurge(confirmPurge)} testId="avi-trash-purge-confirm" />
      </DialogContent>
    </Dialog>
  );
}
